import { useEffect, useRef, useState } from 'react';
import { SwiperRef } from 'swiper/react';
import { Carousel, CarouselSlide } from '../../../components/Carousel';
import ResponsiveImage from '../../../components/ResponsiveImage';
import Modal from '../../Modal';
import ImageViewerPreview, {
  IMAGE_VIEWER_PREVIEW_SIZE,
} from '../ImageViewerPreview';

interface IImage {
  src: string;
  alt?: string | null;
}

interface ImageViewerZoomModalProps {
  images: IImage[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  onSlideChange?: (index: number) => void;
}

const ImageViewerZoomModal = ({
  images,
  initialIndex = 0,
  isOpen,
  onClose,
  onSlideChange,
}: ImageViewerZoomModalProps) => {
  const swiperRef = useRef<SwiperRef>(null);
  const [selectedSlide, setSelectedSlide] = useState<number>(initialIndex);

  const hasMultipleImage = images.length > 1;

  useEffect(() => {
    if (
      swiperRef.current?.swiper &&
      selectedSlide !== swiperRef.current.swiper.activeIndex
    ) {
      swiperRef.current.swiper.slideTo(selectedSlide);
    }
  }, [selectedSlide]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      externalToggle={onClose}
      UNSAFE_className={`
        tw-w-full tw-h-full
        [&_.modal-title-head]:tw-h-[3rem]
        [&_.modal-title-head]:tw-relative
        [&_.modal-title-head]:tw-z-10
        [&_.modal-content]:tw-mt-[calc(50vh-50vw-3rem)]
        max-[800px]:!tw-rounded-none
        min-[960px]:[&_.swiper-pagination]:tw-hidden
        desktop:tw-mt-8 desktop:tw-w-[90vw] desktop:tw-h-auto
        desktop:[&_.modal-title-head]:tw-h-auto
        desktop:[&_.modal-content]:tw-mt-8 desktop:[&_.modal-content]:tw-mb-4
      `}
    >
      <>
        {!hasMultipleImage && (
          <div
            className={`
          tw-h-[100vw-2rem] tw-w-[100vw-2rem]
          desktop:tw-relative desktop:tw-mx-auto
          desktop:tw-my-1 desktop:tw-mb-8 desktop:tw-h-[calc(100vh-8rem)] desktop:tw-w-[calc(100vh-8rem)]
          desktop:tw-overflow-hidden desktop:tw-rounded-lg desktop:tw-outline
          desktop:tw-outline-1 desktop:tw-outline-blue-200
        `}
          >
            <ResponsiveImage
              layout="fill"
              objectFit="contain"
              src={images[0].src}
              alt={images[0].alt || ''}
            />
          </div>
        )}

        {hasMultipleImage && (
          <>
            <Carousel
              ref={swiperRef}
              initialSlide={initialIndex}
              slidesPerView={1}
              pagination
              onSlideChange={({ activeIndex }) => {
                if (activeIndex !== selectedSlide) {
                  setSelectedSlide(activeIndex);
                  onSlideChange?.(activeIndex);
                }
              }}
            >
              {images.map((image, index) => {
                return (
                  <CarouselSlide
                    key={index}
                    className="tw-mb-9 desktop:tw-mb-0"
                  >
                    <div
                      className={`
                    tw-mx-auto
                    tw-h-[calc(100vw-2rem)] tw-w-[calc(100vw-2rem)]
                    desktop:tw-relative desktop:tw-my-1
                    desktop:tw-h-[calc(100vh-3rem-50px-100px)] desktop:tw-w-[calc(100vh-3rem-50px-100px)] desktop:tw-overflow-hidden desktop:tw-rounded-lg
                    desktop:tw-outline desktop:tw-outline-1 desktop:tw-outline-blue-200
                  `}
                    >
                      <ResponsiveImage
                        layout="fill"
                        objectFit="contain"
                        src={image.src}
                        alt={image.alt || ''}
                      />
                    </div>
                  </CarouselSlide>
                );
              })}
            </Carousel>

            <ImageViewerPreview
              images={images}
              selectedSlide={selectedSlide}
              onPreviewClick={(index) => {
                setSelectedSlide(index);
                onSlideChange?.(index);
              }}
              isHorizontal
              size={IMAGE_VIEWER_PREVIEW_SIZE.SMALL}
              UNSAFE_className="tw-mt-3"
            />
          </>
        )}
      </>
    </Modal>
  );
};

export default ImageViewerZoomModal;
